import CheckIcon from "@mui/icons-material/CheckRounded";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import SplitButton from "@entrilia/shared/components/SplitButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../api/adminApi";
import { type ChecklistTask, type ChecklistTaskStatus } from "../../../api/types/objectTypes";
import useUserContext from "../../../context/useUserContext";
import ApprovalActionDialog from "../../approvals/ApprovalActionDialog";
import ChecklistTaskStatusTag from "./ChecklistTaskStatusTag";
import ObjectTasksContext from "./ObjectTasksContext";
import { getTaskStatusOptions, isTaskApprovalAllowed, isTaskStatusChangeAllowed } from "./objectTasksHelper";

const changeChecklistTaskStatuses = withErrorHandling(adminApi.changeChecklistTaskStatuses);

const buttonColorMap: Record<ChecklistTaskStatus | "approval", "primary" | "secondary" | "success" | "warning"> = {
  ToDo: "secondary",
  Done: "success",
  Waived: "warning",
  NotApplicable: "secondary",
  approval: "primary",
};

const statusLabelsMap: Record<ChecklistTaskStatus | "approval", string> = {
  ToDo: "Mark To Do",
  Done: "Mark Done",
  Waived: "Mark Waived",
  NotApplicable: "Mark Not Applicable",
  approval: "Approve",
};

interface Props {
  row: ChecklistTask;
}

const ObjectTasksActions = ({ row }: Props) => {
  const {
    objectType,
    objectId,
    hasPermissionsToManageObjectData,
    onChecklistTasksUpdate,
    onApprovalActionExecuted,
    isSavingTaskId,
    setSavingTaskId,
  } = useDefinedContext(ObjectTasksContext, "ObjectTasksContext");

  const { sendNotificationError } = useNotificationContext();
  const { id: userId } = useUserContext();

  const [showApprovalDialog, setShowApprovalDialog] = useState(false);

  const canApprove = isTaskApprovalAllowed(row, userId);
  const canChangeStatus = hasPermissionsToManageObjectData && isTaskStatusChangeAllowed(row);

  if (!canApprove && !canChangeStatus) {
    return null;
  }

  const handleStatusChange = (targetStatus: ChecklistTaskStatus) => async () => {
    setSavingTaskId(row.id);

    const [data, error] = await changeChecklistTaskStatuses(objectType, objectId, {
      tasks: [{ taskId: row.id, status: targetStatus }],
    });

    setSavingTaskId(undefined);

    if (error) {
      logError(error, "[ObjectTasksActions] handleStatusChange");
      sendNotificationError(error.message);
      return;
    }

    onChecklistTasksUpdate(data);
  };

  const handleApprove = () => {
    setShowApprovalDialog(true);
  };

  const { primaryOption, secondaryOptions } = getTaskStatusOptions(row.status, {
    canApprove,
    canChangeStatus,
  });

  return (
    <>
      <SplitButton
        variant="outlined"
        primaryOption={{
          title: statusLabelsMap[primaryOption],
          handler: primaryOption === "approval" ? handleApprove : handleStatusChange(primaryOption),
        }}
        primaryActionStartIcon={primaryOption === "approval" ? <CheckIcon /> : undefined}
        secondaryOptions={secondaryOptions.map((status) => ({
          title: <ChecklistTaskStatusTag status={status} />,
          handler: handleStatusChange(status),
        }))}
        saving={isSavingTaskId === row.id}
        disabled={isSavingTaskId !== undefined}
        color={buttonColorMap[primaryOption]}
      />
      {showApprovalDialog && row.approvalId && (
        <ApprovalActionDialog
          action="approve"
          approval={{ id: row.approvalId, itemName: `Task ${row.name}` }}
          onClose={() => setShowApprovalDialog(false)}
          onApply={() => onApprovalActionExecuted()}
        />
      )}
    </>
  );
};

export default ObjectTasksActions;
